<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, onMounted, useId } from "vue"
import { useI18n } from "vue-i18n"
import { useConfirm, useToast } from "primevue"
import { useDebounceFn, useElementSize } from "@vueuse/core"
import { inject } from "vue"
import { formatDate, formatIdSource } from "@core/helpers"
import { useFetchWrapper, useRedirect } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import { storeToRefs } from "pinia"
import { useRoute, useRouter } from "vue-router"
import type { ColumnConfig } from "@types"
import type { Services } from "@services"
import {
    getBillingRemoval,
    type BillingSourceRelation,
} from "./billingSourceRemoval"
import {
    celluleFacturation,
    type CelluleFacturation,
    type JugementServi,
} from "./billingSourceJugement"
import {
    CODE_DEMANDE_PLUS_EN_ATTENTE,
    CODE_RELANCE_TROP_TOT,
    demandeEnAttente,
    messageRelanceTropTot,
    phrasePlace,
    typeDeSuppression,
    type DemandeServie,
} from "./billingSourceDemande"
import {
    aDesFiltres,
    celluleAjoutee,
    celluleMiseAJour,
    cellulePlace,
    celluleSiSupprimee,
    celluleSurLeCompte,
    devenirSiSupprimee,
    filtresDepuisUrl,
    FILTRES_BANDEAU,
    largeursColonnes,
    libelleFiltresHorsPastilles,
    lireCompteurs,
    lireCriteres,
    memesFiltres,
    morceauxInsecables,
    pastillesArbre,
    seuilCompletRem,
    texteColonneMiseAJour,
    TITRE_GROUPE_SUR_LE_COMPTE,
    totalAPaginer,
    demanderCompteurs,
    type CellulePlace,
    type CelluleSiSupprimee,
    type CompteursServis,
    type CriteresServis,
    type FiltresActifs,
    type PastilleArbre,
} from "./billingSourceCriteres"
import {
    DEVENIRS_PLACE,
    REPONSES_ARBRE,
    lignesArbre,
    type CompositionDecompte,
    type DevenirPlace,
    type OffreDecompte,
    type PlafondDecompte,
} from "@core/composables/useCompanyPlafond"
import { useResponsive } from "@core/composables"
import { rafraichirPlafondServi } from "@core/composables/plafond-rafraichissement"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Sources de facturation",
            search: "Rechercher une source...",
            no_sources: "Aucune source de facturation trouvée",
            columns: {
                source: "Source",
                type: "Type",
                added_date: "Date d'ajout",
                state: "État",
                billing: "Facturée sur la période",
                facturee: "Facturée",
                mise_a_jour: "Mise à jour",
                place: "Sa place se libère",
                sur_le_compte: "Sur votre compte",
                ajoutee_le: "Ajoutée le",
                mise_a_jour_le: "Mise à jour le",
                si_supprimee: "Si vous la supprimez",
                folder: "Dossier",
                building: "Bâtiment associé",
                owner: "Titulaire",
                actions: "Actions",
            },
            state: {
                active: "Actif",
                removed: "Supprimé",
                pending: "En attente de consentement",
            },
            demande: {
                tooltip_title: "Demande de consentement",
                envoyee_le: "Envoyée le {date}.",
                relancee_le: "Relancée le {date}.",
                relancer: "Relancer la demande",
                supprimer: "Supprimer la demande",
                annuler: "Annuler",
                relancer_titre: "Relancer la demande",
                relancer_message:
                    "Le courriel de demande de consentement sera renvoyé au titulaire. Une demande peut être relancée une fois par jour.",
                relancer_bouton: "Relancer",
                renvoyee: "Demande renvoyée",
                renvoyee_detail:
                    "Le courriel de demande a été renvoyé au titulaire.",
                deja_relancee: "Demande déjà relancée aujourd'hui",
                plus_en_attente:
                    "Cette demande n'est plus en attente de consentement.",
                erreur_relance:
                    "La demande n'a pas pu être renvoyée. Réessayez plus tard.",
                supprimer_titre: "Supprimer la demande",
                supprimer_message: "La demande de consentement sera supprimée.",
                supprimer_bouton: "Supprimer",
                supprimee: "Demande supprimée",
                droits_suppression:
                    "Seul l'auteur de la demande ou un administrateur de votre organisation peut la supprimer.",
                erreur_suppression:
                    "La demande n'a pas pu être supprimée. Réessayez plus tard.",
            },
            filtres: {
                titre: "Filtrer les sources",
                retirer: "Retirer le filtre",
            },
            ajoutee_le_mobile: "Ajoutée le {date}",
            etat_sur_le_compte: "sur votre compte",
            mise_a_jour_tooltip_title: "Mise à jour",
            state_tooltip_title: "Suppression",
            removed_on: "Supprimée le {date}",
            removed_on_by: "Supprimée le {date} par {author}",
            billing: {
                yes: "Oui",
                no: "Non",
            },
            type_filter: {
                types: "Types",
                electrique: "Électrique",
                gaz: "Gaz",
                rcu: "RCU",
                eau: "Eau",
                capteurs: "Capteurs",
                snc: "SNC",
            },
            owner_from_owner: "{details}",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type BillingSource = JugementServi & {
    _id_source: string
    /** Les trois critères de facturation servis (DEV-621, M2), absents sinon */
    criteres?: unknown
    /** Ce que ferait la suppression de la source, servi pour une source sur le compte */
    si_supprimee?: unknown
    /** Demande de consentement en attente (DEV-621), absente sinon */
    demande?: DemandeServie | null
    idsource: string
    idtype: string
    relations?: BillingSourceRelation[]
    buildings?: Array<{
        _id_building: string
        address?: string
        folders?: Array<{
            _id_folder: string
            name: string
        }>
    }>
    owners?: Array<{
        type: "company" | "user"
        company?: { name: string }
        user?: { firstname: string; lastname: string }
    }>
}

type FormattedBillingSource = BillingSource & {
    addedDate: string
    state: { state: boolean; date?: string; removal?: string }
    /** Valeur booléenne pour le filtre BaseTable (true = actif, false = supprimé) */
    stateFilterValue: boolean
    /** Groupe de type pour le filtre (Électrique, Gaz, RCU, Eau si bêta, Capteurs, SNC) */
    typeFilterValue: string
    billing: boolean
    /** Cellule servie par l'API (oui / non et motif), `null` sans jugement servi */
    billingCell: CelluleFacturation | null
    folder: { _id_folder: string; name: string } | null
    building: { _id_building: string; address?: string } | null
    owner: { type: string; details: string } | null
    /** Demande en attente exploitable (relancer, supprimer), `null` sinon */
    demandeEnAttente: DemandeServie | null
    /** Envoi et relance de la demande, en clair, pour l'infobulle de l'état */
    demandeSuivi: string
    /** Critères servis et lisibles, `null` sinon (colonnes d'avant) */
    criteresLus: CriteresServis | null
    /** Cellules des trois critères, `null` sans critères servis */
    cellulesCriteres: {
        surLeCompte: ReturnType<typeof celluleSurLeCompte>
        ajoutee: ReturnType<typeof celluleAjoutee>
        miseAJour: ReturnType<typeof celluleMiseAJour>
    } | null
    /** « Si vous la supprimez », `null` si rien n'est servi (cellule vide) */
    siSupprimeeCell: CelluleSiSupprimee | null
    /** « Facturée » : « Oui », « Oui, jusqu'au JJ/MM » ou « Non » */
    factureeCourte: string
    /** « Sa place se libère », `null` sans critères servis */
    placeCell: CellulePlace | null
    /** « Mise à jour » : « le JJ/MM », « Non », « Non mesuré » ; vide sans critères */
    miseAJourCourt: string
    /** Type de la source en clair (« PDL ») pour la seconde ligne */
    typeCourt: string
    /** « Ajoutée le » : JJ/MM/AAAA, vide sans critères ou sans date servie */
    ajouteeLe: string
    /** Seconde ligne de Source : type · bâtiment · dossier (infobulle native) */
    detailSource: string
    /**
     * État lu sur mobile en seconde ligne de Source : « Ajoutée le
     * JJ/MM/AAAA · sur votre compte » ou « … · supprimée le JJ/MM/AAAA » ;
     * vide sans critères
     */
    etatMobile: string
}

// ================================
// PROPS
// ================================
/**
 * En mode true, le composant est dans un conteneur à hauteur limitée :
 * header/recherche fixes, seul le tableau scrolle. Sinon (page des sources),
 * le tableau prend sa hauteur et la page défile, pagination comprise.
 */
const props = withDefaults(
    defineProps<{
        embedded?: boolean
        /** Fin de la période servie par le bloc `plafond` (DEV-621), si connue */
        periodeFin?: string | null
        /** Bloc `plafond` servi : période et lignes du tableau du décompte (pastilles) */
        plafond?: PlafondDecompte | null
        /** Offre servie (`plafond.offre`) : la colonne « Mise à jour » est EA seulement */
        offre?: OffreDecompte | null
    }>(),
    { embedded: false, periodeFin: null, plafond: null, offre: null }
)

// ================================
// SERVICES & STORES
// ================================
const services = inject<Services>("services")!
const confirm = useConfirm()
const toast = useToast()
/** Groupe du BaseConfirmDialog de la liste */
const componentId = useId()
const authStore = useAuthStore()
const { companyBetaFeatures, companyId } = storeToRefs(authStore)
const { redirectWrapper } = useRedirect()
/** Route courante, absente hors routeur (le filtre d'URL est alors ignoré) */
const route = useRoute()
const router = useRouter()
const { isMobile } = useResponsive()

/**
 * Conteneur du tableau, mesuré (ResizeObserver) : c'est sa largeur, et non
 * celle de la fenêtre, qui décide de la disposition, car la barre latérale
 * change la place disponible
 */
const defilementRef = ref<HTMLElement | null>(null)
const { width: largeurConteneur } = useElementSize(defilementRef)

// ================================
// STATE
// ================================
const sources = ref<BillingSource[]>([])
const total = ref(0)

const pagination = ref({
    page: 1,
    limit: 10,
})

const search = ref("")
/** Demande dont une action (relance, suppression) est en cours */
const demandeEnCours = ref<string | null>(null)
const filtersSelected = ref({
    billing: "",
    state: "",
    idtype: "all",
})
/** Filtres du bandeau (DEV-621, M2), lus d'abord de l'URL (`?si_supprimee=libere`) */
const filtresBandeau = ref<FiltresActifs>(
    filtresDepuisUrl(route?.query as Record<string, unknown> | undefined)
)
/** Nombre de sources par filtre servi avec la page, `null` s'il n'est pas servi */
const compteurs = ref<CompteursServis | null>(null)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Bêta-feature entreprise : propose le filtre type « Eau » (`sigdu` côté API).
 */
const getHasSigduBeta = computed(
    () => companyBetaFeatures.value?.sigdu === true
)

/**
 * Formate les sources avec les données calculées
 */
const getSourcesFormatted = computed<FormattedBillingSource[]>(() => {
    return sources.value.map((source) => {
        const state = getState(source)
        const billingCell = celluleFacturation(source, props.periodeFin)
        const criteresLus = lireCriteres(source.criteres)
        const billing = billingCell?.facturee ?? isBillingThisMonth(source)
        const siSupprimeeCell = celluleSiSupprimee(
            source.si_supprimee,
            props.periodeFin,
            criteresLus?.sur_le_compte ?? null
        )
        return {
            ...source,
            addedDate: getAddedDate(source),
            state,
            stateFilterValue: state.state,
            typeFilterValue: getTypeFilterValue(source.idtype),
            // DEV-621 : le jugement servi par l'API fait foi ; le calcul
            // historique ne sert que tant que l'API ne le sert pas.
            billing,
            billingCell,
            folder: getFolder(source),
            building: getBuilding(source),
            owner: getOwner(source),
            demandeEnAttente: demandeEnAttente(source),
            demandeSuivi: getDemandeSuivi(source),
            criteresLus,
            cellulesCriteres: criteresLus
                ? {
                      surLeCompte: celluleSurLeCompte(criteresLus),
                      ajoutee: celluleAjoutee(criteresLus),
                      miseAJour: celluleMiseAJour(criteresLus),
                  }
                : null,
            siSupprimeeCell,
            factureeCourte: !billing
                ? REPONSES_ARBRE.facturee.non
                : (billingCell?.reponse ?? REPONSES_ARBRE.facturee.oui),
            placeCell: criteresLus
                ? cellulePlace(
                      criteresLus.sur_le_compte,
                      siSupprimeeCell,
                      billing,
                      props.periodeFin
                  )
                : null,
            miseAJourCourt: criteresLus
                ? texteColonneMiseAJour(criteresLus)
                : "",
            typeCourt: source.idtype?.toUpperCase() ?? "",
            ajouteeLe: criteresLus ? celluleAjoutee(criteresLus).date : "",
            detailSource: [
                source.idtype?.toUpperCase() ?? "",
                getBuilding(source)?.address ?? "",
                getFolder(source)?.name ?? "",
            ]
                .filter(Boolean)
                .join(" · "),
            etatMobile: criteresLus ? getEtatMobile(criteresLus) : "",
        }
    })
})

/**
 * Vrai si l'API sert les critères (sur une ligne au moins) : la liste montre
 * alors les colonnes des trois critères à la place de « Date d'ajout » et
 * « État ».
 */
const getHasCriteres = computed(() =>
    getSourcesFormatted.value.some((s) => s.criteresLus !== null)
)

/** Vrai si l'API sert, pour une ligne au moins, ce que ferait sa suppression */
const getHasSiSupprimee = computed(() =>
    getSourcesFormatted.value.some((s) => s.siSupprimeeCell !== null)
)

/**
 * Le bandeau de filtres n'apparaît que si l'API sert les critères ou les
 * compteurs : sans eux, un filtre ne serait pas appliqué par le serveur.
 */
const getHasBandeau = computed(
    () => getHasCriteres.value || compteurs.value !== null
)

/** Composition sans nombre : les filtres des lignes n'en dépendent pas */
const COMPOSITION_VIDE: CompositionDecompte = {
    sur_le_compte_ajoutees_dans_la_periode: 0,
    sur_le_compte_ajoutees_avant: 0,
    supprimees_ajoutees_dans_la_periode: 0,
    supprimees_apres_mise_a_jour: 0,
    supprimees_liberees: 0,
}

/**
 * Pastilles sans nombre, tirées des lignes du tableau du décompte qui portent
 * des filtres (revue UX du 15/10) ; « Toutes » seule sans offre servie
 */
const getPastilles = computed((): PastilleArbre[] => {
    const p = props.plafond
    if (!props.offre) return pastillesArbre([])
    return pastillesArbre(
        lignesArbre(
            p?.composition ?? COMPOSITION_VIDE,
            props.offre,
            p?.periode_debut ?? null,
            p?.periode_fin ?? props.periodeFin
        ),
        p?.periode_debut ?? null
    )
})

/**
 * Filtres actifs qui ne sont ceux d'aucune pastille (lien de courriel
 * `?si_supprimee=libere`) : leur libellé, affiché en pastille active avec une
 * croix ; `null` sinon
 */
const getPastilleHors = computed((): string | null => {
    if (!aDesFiltres(filtresBandeau.value)) return null
    if (
        getPastilles.value.some((p) =>
            memesFiltres(p.filtres, filtresBandeau.value)
        )
    ) {
        return null
    }
    return libelleFiltresHorsPastilles(filtresBandeau.value)
})

/** Seul nombre écrit au-dessus du tableau : `compteurs.filtrees`, rien sinon */
const getNombreFiltrees = computed((): number | null =>
    typeof compteurs.value?.filtrees === "number"
        ? compteurs.value.filtrees
        : null
)

/** « 473 sources », « 1 source » */
const getTexteNombreFiltrees = computed((): string | null => {
    const n = getNombreFiltrees.value
    if (n === null) return null
    return n > 1 ? `${n} sources` : `${n} source`
})

/** Colonne « Mise à jour » : critère Energy Analytics seulement, absente en EM */
const getAvecMiseAJour = computed(() => props.offre !== "EM")

/**
 * Vrai si une ligne au moins porte une demande en attente servie : la
 * colonne des actions n'apparaît pas tant que l'API ne sert pas `demande`.
 */
const getHasDemandes = computed(() =>
    getSourcesFormatted.value.some((s) => s.demandeEnAttente !== null)
)

/** Demi-largeur de l'hystérésis de bascule compacte, en pixels */
const HYSTERESIS_PX = 8

/** Taille d'un rem en pixels (16 par défaut) */
function remEnPx(): number {
    const taille = Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize
    )
    return Number.isFinite(taille) && taille > 0 ? taille : 16
}

/**
 * Disposition compacte décidée par la largeur mesurée, avec une hystérésis
 * de 16 px autour du seuil (`HYSTERESIS_PX` de part et d'autre) : on passe en
 * compacte sous seuil moins 8 px et on revient en complète au-dessus de seuil plus
 * 8 px, pour qu'une largeur qui oscille autour du seuil (barre de défilement
 * qui apparaît, arrondi de zoom) ne fasse pas scintiller la liste. Largeur
 * non mesurée (0) : disposition complète.
 */
const compacteMesuree = ref(false)
watch(
    () => [
        largeurConteneur.value,
        seuilCompletRem(props.offre, getHasDemandes.value) * remEnPx(),
    ],
    ([largeur, seuil]) => {
        if (!(largeur > 0)) {
            compacteMesuree.value = false
            return
        }
        if (compacteMesuree.value) {
            if (largeur > seuil + HYSTERESIS_PX) compacteMesuree.value = false
        } else if (largeur < seuil - HYSTERESIS_PX) {
            compacteMesuree.value = true
        }
    },
    { immediate: true }
)

/**
 * Disposition compacte (Source avec l'état et la date d'ajout en seconde
 * ligne, Facturée, Sa place se libère) : sur mobile, ou quand le conteneur
 * mesuré est plus étroit que le seuil de la disposition complète (EA 60rem,
 * EM 51rem, plus la colonne Actions), au lieu de se tasser ou de défiler.
 */
const getCompacte = computed(
    (): boolean => isMobile.value || compacteMesuree.value
)

/**
 * Colonnes avec critères servis (demande du 16/10) : Source (type, bâtiment
 * et dossier en seconde ligne), Ajoutée le, Sur votre compte, Mise à jour
 * (EA seulement), Facturée, Sa place se libère ; sur mobile, Source,
 * Facturée et Sa place se libère. Type, Dossier, Bâtiment et Titulaire
 * restent dans l'export. Aucune largeur ici : elles sont posées en
 * pourcentage sur les en-têtes (`getStyleTableau`), car BaseTable reporte
 * le style d'une colonne sur le contenu de ses cellules.
 */
const colonnesCriteres = computed<ColumnConfig[]>(() => {
    const colonne = (field: string, header: string): ColumnConfig => ({
        field,
        header: t(header),
        sortable: false,
    })
    const source = colonne("idsource", "columns.source")
    const facturee = colonne("billing", "columns.facturee")
    const place = colonne("place", "columns.place")
    if (getCompacte.value) return [source, facturee, place]
    return [
        source,
        colonne("ajouteeLe", "columns.ajoutee_le"),
        colonne("surLeCompte", "columns.sur_le_compte"),
        ...(getAvecMiseAJour.value
            ? [colonne("miseAJourLe", "columns.mise_a_jour")]
            : []),
        facturee,
        place,
    ]
})

/**
 * Largeurs des colonnes avec critères, en valeurs CSS (rem en disposition
 * complète, pourcentages en compacte), dans l'ordre des colonnes, Actions
 * comprise ; `null` sans critères servis
 */
const getLargeursColonnes = computed((): string[] | null =>
    getHasCriteres.value
        ? largeursColonnes(props.offre, getCompacte.value, getHasDemandes.value)
        : null
)

/**
 * Largeur minimale du tableau avec critères, en disposition complète : le
 * seuil (Source au minimum plus les colonnes bornées : EA 60rem, EM 51rem,
 * plus 14rem avec la colonne Actions) ; en disposition compacte, aucune,
 * sauf avec la colonne Actions (36rem).
 */
const getLargeurMinTableau = computed((): string | null => {
    if (!getHasCriteres.value) return null
    if (getCompacte.value) return getHasDemandes.value ? "36rem" : null
    return `${seuilCompletRem(props.offre, getHasDemandes.value)}rem`
})

/**
 * Style du tableau (BaseTable n'expose pas `tableStyle`) : largeur
 * minimale et largeur de chaque colonne, lues par la feuille de style
 * (`table-layout: fixed`)
 */
const getStyleTableau = computed((): Record<string, string> => {
    const largeurs = getLargeursColonnes.value
    if (!largeurs) return {}
    return {
        ...(getLargeurMinTableau.value
            ? { "--liste-largeur-min": getLargeurMinTableau.value }
            : {}),
        ...Object.fromEntries(
            largeurs.map((l, i) => [`--liste-colonne-${i + 1}`, l])
        ),
    }
})

/**
 * Configuration des colonnes pour BaseTable
 */
const billingSourceColumns = computed<ColumnConfig[]>(() => {
    const actions: ColumnConfig[] = getHasDemandes.value
        ? [
              {
                  field: "actions",
                  header: t("columns.actions"),
                  sortable: false,
                  ...(getHasCriteres.value
                      ? {}
                      : { style: { width: "16rem" } }),
              },
          ]
        : []
    if (getHasCriteres.value) return [...colonnesCriteres.value, ...actions]
    const typeFilterOptions: Array<{ label: string; value: string }> = [
        { label: t("type_filter.electrique"), value: "Électrique" },
        { label: t("type_filter.gaz"), value: "Gaz" },
        { label: t("type_filter.rcu"), value: "RCU" },
    ]
    if (getHasSigduBeta.value) {
        typeFilterOptions.push({ label: t("type_filter.eau"), value: "Eau" })
    }
    typeFilterOptions.push(
        { label: t("type_filter.capteurs"), value: "Capteurs" },
        { label: t("type_filter.snc"), value: "SNC" }
    )
    return [
        {
            field: "idsource",
            header: t("columns.source"),
            sortable: false,
            style: { width: "15rem" },
        },
        {
            field: "typeFilterValue",
            header: t("columns.type"),
            sortable: false,
            style: { width: "10rem" },
            filterable: true,
            filterOptions: typeFilterOptions,
        },
        {
            field: "addedDate",
            header: t("columns.added_date"),
            sortable: false,
            style: { width: "7rem" },
        },
        {
            field: "stateFilterValue",
            header: t("columns.state"),
            sortable: false,
            style: { width: "7rem" },
            filterable: true,
            filterOptions: [
                { label: t("state.active"), value: true },
                { label: t("state.removed"), value: false },
            ],
        },
        {
            field: "billing",
            header: t("columns.billing"),
            sortable: false,
            style: { width: "16rem" },
            filterable: true,
            filterOptions: [
                { label: t("billing.yes"), value: true },
                { label: t("billing.no"), value: false },
            ],
        },
        ...(getHasSiSupprimee.value
            ? [
                  {
                      field: "siSupprimee",
                      header: t("columns.si_supprimee"),
                      sortable: false,
                      style: { width: "14rem" },
                  },
              ]
            : []),
        {
            field: "folder",
            header: t("columns.folder"),
            sortable: false,
            style: { width: "14rem" },
        },
        {
            field: "building",
            header: t("columns.building"),
            sortable: false,
            style: { width: "18rem" },
        },
        {
            field: "owner",
            header: t("columns.owner"),
            sortable: false,
            style: { width: "14rem" },
        },
        ...actions,
    ]
})

// ================================
// FETCH DATA
// ================================

/**
 * Récupère les sources de facturation depuis l'API
 */
const { fetch: fetchSources, isFetching: fetchState } = useFetchWrapper(
    async () => {
        try {
            const response = await services.$getBillingSource({
                page: pagination.value.page,
                limit: pagination.value.limit,
                search: search.value || null,
                status: filtersSelected.value.state as
                    | ""
                    | "active"
                    | "removed",
                date: filtersSelected.value.billing as
                    | ""
                    | "current_month"
                    | "not_current_month",
                idtype: filtersSelected.value.idtype,
                criteres: filtresBandeau.value,
                // nombres par filtre : première page et changement de filtre
                // seulement, les autres pages gardent ceux déjà lus
                compteurs: demanderCompteurs(
                    pagination.value.page,
                    compteurs.value
                ),
            })

            if (typeof response === "number") {
                total.value = response
                return
            }

            const { data } = response
            const lus = lireCompteurs(response.compteurs)
            if (lus || pagination.value.page <= 1) compteurs.value = lus
            const criteresServis = (data ?? []).some(
                (s: BillingSource) => lireCriteres(s?.criteres) !== null
            )
            // Filtre demandé (URL, bandeau) mais ni critères ni compteurs
            // servis : le serveur ne l'applique pas ; on le retire et on relit
            // la liste entière plutôt que de la présenter comme filtrée.
            if (
                aDesFiltres(filtresBandeau.value) &&
                !criteresServis &&
                compteurs.value === null
            ) {
                filtresBandeau.value = {}
                void remplacerFiltresUrl({})
                total.value = 0
                await fetchSources()
                return
            }

            // Liste jugée sur l'ensemble : `filtrees` est le nombre à paginer ;
            // sans lui, avec un filtre de critère, ce qui est servi
            // (`count=true` ignore les filtres de critères)
            const aPaginer = totalAPaginer(compteurs.value, {
                filtresCriteres: aDesFiltres(filtresBandeau.value),
                page: pagination.value.page,
                limit: pagination.value.limit,
                servies: (data ?? []).length,
                aSuivante: response.has_more === true,
            })
            if (aPaginer !== null) {
                total.value = aPaginer
            } else if (total.value === 0) {
                const countResponse = await services.$getBillingSource({
                    search: search.value || null,
                    count: true,
                    status: filtersSelected.value.state as
                        | ""
                        | "active"
                        | "removed",
                    date: filtersSelected.value.billing as
                        | ""
                        | "current_month"
                        | "not_current_month",
                    idtype: filtersSelected.value.idtype,
                    criteres: filtresBandeau.value,
                })
                if (typeof countResponse === "number") {
                    total.value = countResponse
                }
            }

            sources.value = data
        } catch (err) {
            console.error("Error fetching billing sources:", err)
        }
    }
)

// ================================
// HELPER FUNCTIONS
// ================================

/**
 * Récupère la relation courante d'une source (triée par date puis dismissed)
 */
function getCurrentRelation(source: BillingSource) {
    const relations = [...(source?.relations || [])]

    if (relations.length === 0) {
        return undefined
    }

    // Trie par date de création (plus récent en premier)
    relations.sort(
        (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    // Trie par statut (non dismissed en premier)
    relations.sort((a, b) => {
        if (a.dismissed && !b.dismissed) return 1
        if (!a.dismissed && b.dismissed) return -1
        return 0
    })

    return relations[0]
}

/**
 * Retourne le libellé de groupe pour un idtype (pour le filtre colonne Type).
 * Aligné sur les options du legacy : Électrique (pdl, elecsavoie, elecstrasbourg), Gaz (pce, regaz), etc.
 */
function getTypeFilterValue(idtype: string): string {
    const groupByType: Record<string, string> = {
        pdl: "Électrique",
        elecsavoie: "Électrique",
        elecstrasbourg: "Électrique",
        pce: "Gaz",
        regaz: "Gaz",
        cpcu: "RCU",
        dalkia: "RCU",
        sigdu: "Eau",
        iot: "Capteurs",
        snc: "SNC",
    }
    return groupByType[idtype] ?? idtype
}

/**
 * Récupère la date d'ajout formatée
 */
function getAddedDate(source: BillingSource): string {
    const relation = getCurrentRelation(source)
    if (!relation) return ""
    return formatDate(relation.createdAt, "DD/MM/YYYY", "-")
}

/**
 * Récupère l'état de la source (actif ou retiré avec date)
 */
function getState(source: BillingSource): {
    state: boolean
    date?: string
    removal?: string
} {
    const relation = getCurrentRelation(source)
    if (!relation) {
        // Si pas de relation, on considère la source comme active par défaut
        return {
            state: true,
        }
    }
    const removal = getBillingRemoval(relation)
    if (removal) {
        // DEV-2251 : le serveur envoie `dismissed_at` (et non `dismissedAt`) et
        // l'auteur déjà résolu en nom ; sans auteur connu, la date seule.
        let label: string | undefined
        if (removal.date) {
            label = removal.author
                ? t("removed_on_by", {
                      date: removal.date,
                      author: removal.author,
                  })
                : t("removed_on", { date: removal.date })
        }
        return {
            state: false,
            date: removal.date || undefined,
            removal: label,
        }
    } else {
        return {
            state: true,
        }
    }
}

/**
 * Vérifie si la source est facturée pendant la période
 */
function isBillingThisMonth(source: BillingSource): boolean {
    const relation = getCurrentRelation(source)
    if (!relation) return false
    if (!relation.dismissed) return true

    // Vérifie si la date de création est dans la période de facturation courante
    const currentOffer = authStore.getCurrentOffer ?? null
    if (!currentOffer?.current_start_period_monthly) return false

    const relationDate = new Date(relation.createdAt)
    const startPeriod = new Date(currentOffer.current_start_period_monthly)
    const endPeriod = currentOffer.current_end_period_monthly
        ? new Date(currentOffer.current_end_period_monthly)
        : new Date()

    return relationDate >= startPeriod && relationDate <= endPeriod
}

/**
 * Récupère le dossier de la source
 */
function getFolder(source: BillingSource) {
    if (!source?.buildings?.length || !source.buildings[0].folders?.length) {
        return null
    }

    return source.buildings[0].folders[0]
}

/**
 * Récupère le bâtiment de la source
 */
function getBuilding(source: BillingSource) {
    if (!source?.buildings?.length) {
        return null
    }
    return source.buildings[0]
}

/**
 * Seconde ligne de Source sur mobile : la date d'ajout puis l'état,
 * « Ajoutée le JJ/MM/AAAA · sur votre compte » ou « Ajoutée le JJ/MM/AAAA ·
 * supprimée le JJ/MM/AAAA » ; sans date servie, l'état seul
 */
function getEtatMobile(c: CriteresServis): string {
    const sur = celluleSurLeCompte(c)
    const date = celluleAjoutee(c).date
    const etat = sur.surLeCompte ? t("etat_sur_le_compte") : sur.texte
    if (!date) return etat.charAt(0).toUpperCase() + etat.slice(1)
    const suite = sur.surLeCompte
        ? etat
        : etat.charAt(0).toLowerCase() + etat.slice(1)
    return `${t("ajoutee_le_mobile", { date })} · ${suite}`
}

/**
 * Étiquette d'un devenir de la place (référentiel `DEVENIRS_PLACE`, mêmes
 * couleurs que la barre et le tableau du décompte)
 * @param devenir - devenir de la place
 */
function etiquetteDevenir(devenir: DevenirPlace | null) {
    const d = devenir ? DEVENIRS_PLACE[devenir] : null
    return { severity: d?.severite, class: d?.classeEtiquette }
}

/**
 * Envoi et relance de la demande en attente, en clair (infobulle de l'état)
 */
function getDemandeSuivi(source: BillingSource): string {
    const demande = demandeEnAttente(source)
    if (!demande) return ""
    const envoyee = formatDate(demande.envoyee_le, "DD/MM/YYYY", "")
    const relancee = formatDate(demande.relancee_le, "DD/MM/YYYY", "")
    return [
        envoyee ? t("demande.envoyee_le", { date: envoyee }) : "",
        relancee ? t("demande.relancee_le", { date: relancee }) : "",
    ]
        .filter(Boolean)
        .join(" ")
}

/**
 * Récupère le titulaire de la source
 */
function getOwner(source: BillingSource) {
    if (!source?.owners?.length) {
        return null
    }

    const owner = source.owners[0]
    if (owner.type === "company") {
        return {
            type: owner.type,
            details: owner.company?.name || "",
        }
    } else {
        return {
            type: owner.type,
            details:
                `${owner.user?.firstname || ""} ${owner.user?.lastname || ""}`.trim(),
        }
    }
}

// ================================
// METHODS
// ================================

/**
 * Map groupe de type (option filtre) → idtype API (un représentant)
 */
const TYPE_GROUP_TO_IDTYPE: Record<string, string> = {
    Électrique: "pdl",
    Gaz: "pce",
    RCU: "cpcu",
    Eau: "sigdu",
    Capteurs: "iot",
    SNC: "snc",
}

/**
 * À partir des filtres du tableau (emit BaseTable), met à jour filtersSelected et refetch
 * Le fetch est debouncé pour permettre de sélectionner plusieurs filtres avant de charger
 */
function onTableFiltersChange(
    tableFilters: Record<string, { value: unknown }>
) {
    const stateVal = tableFilters.stateFilterValue?.value
    if (Array.isArray(stateVal) && stateVal.length === 1) {
        filtersSelected.value.state =
            stateVal[0] === true ? "active" : "removed"
    } else {
        filtersSelected.value.state = ""
    }

    const billingVal = tableFilters.billing?.value
    if (Array.isArray(billingVal) && billingVal.length === 1) {
        filtersSelected.value.billing =
            billingVal[0] === true ? "current_month" : "not_current_month"
    } else {
        filtersSelected.value.billing = ""
    }

    const typeVal = tableFilters.typeFilterValue?.value
    if (Array.isArray(typeVal) && typeVal.length === 1 && typeVal[0]) {
        filtersSelected.value.idtype =
            TYPE_GROUP_TO_IDTYPE[String(typeVal[0])] ?? "all"
    } else {
        filtersSelected.value.idtype = "all"
    }

    pagination.value.page = 1
    total.value = 0
    // Ne pas vider sources : on garde les données affichées pendant le fetch
    // pour éviter le skeleton initial, le loading overlay suffit
    debouncedFetchSources()
}

/** Vrai si la pastille est active : les filtres actifs sont exactement les siens */
function estPastilleActive(pastille: PastilleArbre): boolean {
    return memesFiltres(filtresBandeau.value, pastille.filtres)
}

/** Paramètres de requête des filtres du bandeau */
const PARAMETRES_FILTRES = new Set<string>(
    FILTRES_BANDEAU.map((f) => f.parametre)
)

/**
 * Écrit les filtres dans la query de la route (la page des sources les y
 * garde : lien partageable, retour arrière), sans toucher au reste ; la
 * liste suit la query (`watch` plus bas). Vrai si l'URL a pu être écrite.
 * @param filtres - filtres du bandeau, `{}` pour aucun
 */
async function remplacerFiltresUrl(filtres: FiltresActifs): Promise<boolean> {
    if (!route || !router) return false
    const query: Record<string, unknown> = {}
    for (const [cle, valeur] of Object.entries(route.query)) {
        if (!PARAMETRES_FILTRES.has(cle)) query[cle] = valeur
    }
    Object.assign(query, filtres)
    try {
        await router.replace({
            query: query as Record<string, string>,
            hash: route.hash,
        })
        return true
    } catch {
        return false
    }
}

/**
 * Applique des filtres : par la query de la route quand il y en a une (la
 * liste la suit), sinon directement (hors routeur)
 * @param filtres - filtres à poser
 */
async function appliquerFiltres(filtres: FiltresActifs) {
    if (!(await remplacerFiltresUrl(filtres))) poserFiltres(filtres)
}

/** Pose des filtres (remplace les précédents) et relit la première page */
function poserFiltres(filtres: FiltresActifs) {
    if (memesFiltres(filtresBandeau.value, filtres)) return
    filtresBandeau.value = { ...filtres }
    pagination.value.page = 1
    total.value = 0
    debouncedFetchSources()
}

/**
 * Pastille cliquée : ses filtres remplacent les filtres actifs ; cliquée
 * active, elle se retire (retour à « Toutes »)
 * @param pastille - pastille cliquée
 */
function onChoisirPastille(pastille: PastilleArbre) {
    void appliquerFiltres(estPastilleActive(pastille) ? {} : pastille.filtres)
}

/**
 * La query change (pastille, lien de la barre de synthèse, retour arrière) :
 * la liste pose ses filtres
 */
watch(
    () => route?.query,
    (query) => {
        poserFiltres(filtresDepuisUrl(query as Record<string, unknown>))
    }
)

/**
 * Refetch des sources debouncé (filtres tableau) pour éviter des requêtes à chaque changement
 */
const debouncedFetchSources = useDebounceFn(() => {
    fetchSources()
}, 400)

/**
 * Retire le filtre type Eau (`sigdu`) si la bêta-feature n'est pas active pour l'entreprise.
 */
watch(
    [getHasSigduBeta, companyId],
    () => {
        if (!companyId.value) return
        if (
            !getHasSigduBeta.value &&
            filtersSelected.value.idtype === "sigdu"
        ) {
            filtersSelected.value.idtype = "all"
            debouncedFetchSources()
        }
    },
    { immediate: true }
)

/**
 * Gère la recherche avec debounce
 */
const onSearch = useDebounceFn((value: string) => {
    search.value = value
    pagination.value.page = 1
    total.value = 0
    sources.value = []
    fetchSources()
}, 1000)

/**
 * Gère le changement de page du tableau (pagination côté serveur)
 * @param event - Event PrimeVue { first, rows }
 */
function onPage(event: { first: number; rows: number }) {
    pagination.value.page = Math.floor(event.first / event.rows) + 1
    pagination.value.limit = event.rows
    fetchSources()
}

/**
 * Gère le clic sur une source pour naviguer vers la page de la source
 * @param sourceId - Identifiant de la source
 */
function onClickSource(sourceId: string) {
    redirectWrapper("/source/:_id_source/data", { _id_source: sourceId })
}

/**
 * Gère le clic sur un dossier pour naviguer vers la page du dossier
 * @param folderId - Identifiant du dossier
 */
function onClickFolder(folderId: string) {
    redirectWrapper("/folder/:_id_folder", {
        _id_folder: folderId,
        _blank: true,
    })
}

/**
 * Gère le clic sur un bâtiment pour naviguer vers la page du bâtiment
 * @param buildingId - Identifiant du bâtiment
 */
function onClickBuilding(buildingId: string) {
    redirectWrapper("/building/:_id_building/data", {
        _id_building: buildingId,
        _blank: true,
    })
}

/**
 * Demande la confirmation puis relance la demande en attente (DEV-621, M2)
 * @param demande - demande en attente de la ligne
 */
function onRelancerDemande(demande: DemandeServie) {
    confirm.require({
        group: componentId,
        header: t("demande.relancer_titre"),
        message: t("demande.relancer_message"),
        icon: "pi pi-send",
        rejectProps: {
            label: t("demande.annuler"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: { label: t("demande.relancer_bouton") },
        accept: () => relancerDemande(demande),
    })
}

/**
 * Relit la liste après une mutation faite depuis elle (suppression, relance,
 * demande plus en attente) : les compteurs lus sont périmés, ils sont
 * redemandés (`compteurs=oui`) quelle que soit la page. La page reste la même,
 * sauf si elle est devenue vide : on revient alors à la dernière page servie.
 */
async function relireApresMutation() {
    compteurs.value = null
    total.value = 0
    await fetchSources()
    if (sources.value.length === 0 && pagination.value.page > 1) {
        const derniere = Math.max(
            1,
            Math.ceil(total.value / pagination.value.limit)
        )
        pagination.value.page = Math.min(pagination.value.page - 1, derniere)
        await fetchSources()
    }
}

/**
 * Relance la demande : « Demande renvoyée », ou le refus lisible
 * (« déjà relancée aujourd'hui », demande plus en attente)
 */
async function relancerDemande(demande: DemandeServie) {
    demandeEnCours.value = demande._id
    try {
        await services.$relancerConsentRequest(demande._id)
        toast.add({
            severity: "success",
            summary: t("demande.renvoyee"),
            detail: t("demande.renvoyee_detail"),
            life: 5000,
        })
        await relireApresMutation()
    } catch (err) {
        const code = (err as { code?: string })?.code
        if (code === CODE_RELANCE_TROP_TOT) {
            toast.add({
                severity: "warn",
                summary: t("demande.deja_relancee"),
                detail: messageRelanceTropTot(
                    (err as { context?: unknown }).context
                ),
                life: 6000,
            })
        } else if (code === CODE_DEMANDE_PLUS_EN_ATTENTE) {
            toast.add({
                severity: "info",
                summary: t("demande.plus_en_attente"),
                life: 5000,
            })
            await relireApresMutation()
        } else {
            toast.add({
                severity: "error",
                summary: t("demande.erreur_relance"),
                life: 6000,
            })
        }
    } finally {
        demandeEnCours.value = null
    }
}

/**
 * Demande la confirmation, qui dit ce que devient la place, puis supprime
 * la demande en attente (DEV-621, M2 et M6)
 * @param demande - demande en attente de la ligne
 */
function onSupprimerDemande(demande: DemandeServie) {
    confirm.require({
        group: componentId,
        header: t("demande.supprimer_titre"),
        message: [t("demande.supprimer_message"), phrasePlace(demande)]
            .filter(Boolean)
            .join(" "),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("demande.annuler"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: {
            label: t("demande.supprimer_bouton"),
            severity: "danger",
            icon: "pi pi-trash",
        },
        accept: () => supprimerDemande(demande),
    })
}

/**
 * Supprime la demande par la route existante, relit la liste et le plafond
 * servi (la place libérée change le décompte)
 */
async function supprimerDemande(demande: DemandeServie) {
    demandeEnCours.value = demande._id
    try {
        await services.$deleteConsentRequest(
            demande._id,
            typeDeSuppression(demande)
        )
        toast.add({
            severity: "success",
            summary: t("demande.supprimee"),
            life: 5000,
        })
        await relireApresMutation()
        await rafraichirPlafondServi()
    } catch (err) {
        const code = (err as { code?: string })?.code
        const status = (err as { status?: number | null })?.status
        toast.add({
            severity: "error",
            summary:
                code === "restricted_user_consentrequest"
                    ? t("demande.droits_suppression")
                    : t("demande.erreur_suppression"),
            life: 6000,
        })
        // Demande déjà signée (400) ou déjà supprimée (404) : la liste est
        // relue pour que la ligne et ses boutons disparaissent
        if (status === 400 || status === 404) {
            await relireApresMutation()
        }
    } finally {
        demandeEnCours.value = null
    }
}

// ================================
// EXPOSE
// ================================
defineExpose({
    total,
})

// ================================
// LIFECYCLE
// ================================

onMounted(() => {
    fetchSources()
})
</script>

<template>
    <div
        class="company-billing-source-list flex flex-col gap-3"
        :class="{
            'flex-1 min-h-0 overflow-hidden': props.embedded,
        }"
    >
        <!-- ================================ -->
        <!-- PASTILLES DE L'ARBRE, SANS NOMBRE (revue UX du 15/10) -->
        <!-- ================================ -->
        <div
            v-if="getHasBandeau"
            class="company-billing-source-list__filtres flex items-center gap-2"
            :class="isMobile ? 'flex-nowrap overflow-x-auto pb-1' : 'flex-wrap'"
            role="group"
            :aria-label="t('filtres.titre')"
        >
            <template
                v-for="(pastille, index) in getPastilles"
                :key="pastille.id"
            >
                <span
                    v-if="
                        pastille.groupe &&
                        getPastilles[index - 1]?.groupe !== pastille.groupe
                    "
                    class="company-billing-source-list__groupe shrink-0 whitespace-nowrap pl-1 text-sm text-surface-600"
                    aria-hidden="true"
                >
                    {{ TITRE_GROUPE_SUR_LE_COMPTE }}
                </span>
                <BaseButton
                    :label="pastille.court"
                    size="small"
                    rounded
                    :severity="
                        estPastilleActive(pastille) ? undefined : 'secondary'
                    "
                    :outlined="!estPastilleActive(pastille)"
                    class="company-billing-source-list__filtre shrink-0 whitespace-nowrap"
                    :aria-pressed="estPastilleActive(pastille)"
                    :aria-label="pastille.libelle"
                    :title="pastille.libelle"
                    :data-filtre="pastille.id"
                    @click="onChoisirPastille(pastille)"
                />
            </template>
            <BaseButton
                v-if="getPastilleHors"
                :label="getPastilleHors"
                size="small"
                rounded
                icon="pi pi-times"
                icon-pos="right"
                class="company-billing-source-list__filtre company-billing-source-list__filtre-hors shrink-0 whitespace-nowrap"
                aria-pressed="true"
                :aria-label="`${getPastilleHors}. ${t('filtres.retirer')}`"
                data-filtre="hors"
                @click="appliquerFiltres({})"
            />
        </div>

        <!-- ================================ -->
        <!-- RECHERCHE ET ACTIONS (Exporter, sur la même rangée) -->
        <!-- ================================ -->
        <div
            class="company-billing-source-list__barre flex flex-wrap items-center justify-between gap-3"
        >
            <div
                class="company-billing-source-list__search max-w-md min-w-[12rem] flex-1"
            >
                <BaseInputWrapper input-id="search-billing-source">
                    <template #prefix>
                        <BaseInputGroupAddon>
                            <BaseIcon icon="pi pi-search" />
                        </BaseInputGroupAddon>
                    </template>
                    <BaseInputText
                        id="search-billing-source"
                        v-model="search"
                        :placeholder="t('search')"
                        fluid
                        @update:model-value="onSearch"
                    />
                </BaseInputWrapper>
            </div>
            <slot name="actions" />
        </div>

        <!-- Seul nombre : la liste filtrée servie (`compteurs.filtrees`) -->
        <p
            v-if="getTexteNombreFiltrees"
            class="company-billing-source-list__nombre text-sm text-surface-600 tabular-nums"
        >
            {{ getTexteNombreFiltrees }}
        </p>

        <!-- ================================ -->
        <!-- TABLEAU (seule zone scrollable en mode embedded) -->
        <!-- ================================ -->
        <div
            class="company-billing-source-list__table flex flex-col min-h-0"
            :class="props.embedded ? 'flex-1 overflow-hidden' : ''"
        >
            <!-- Tableau (toujours affiché, loading overlay pendant le fetch) -->
            <div
                ref="defilementRef"
                class="company-billing-source-list__defilement overflow-auto overflow-x-auto min-h-0"
                :class="[
                    props.embedded ? 'flex-1' : '',
                    getLargeursColonnes
                        ? 'company-billing-source-list__defilement--fixe'
                        : '',
                ]"
                :style="getStyleTableau"
            >
                <BaseTable
                    data-key="_id_source"
                    :columns="billingSourceColumns"
                    :value="getSourcesFormatted"
                    :total-records="total"
                    :lazy="true"
                    :first="(pagination.page - 1) * pagination.limit"
                    :loading="fetchState"
                    paginator
                    :rows="pagination.limit"
                    :rows-per-page-options="[10, 25, 50]"
                    scrollable
                    :empty-message="t('no_sources')"
                    class="w-full"
                    @page="onPage"
                    @update:filters="onTableFiltersChange"
                >
                    <template #cell_idsource="{ data }">
                        <div
                            v-if="data.cellulesCriteres"
                            class="company-billing-source-list__source min-w-0 flex-1"
                        >
                            <div class="flex min-w-0 items-center gap-2">
                                <BaseSourceIcon
                                    :type="data.idtype"
                                    size="20"
                                    class="flex-shrink-0"
                                />
                                <span
                                    v-if="data.state.state"
                                    class="text-primary hover:underline cursor-pointer min-w-0 block"
                                    :class="
                                        getCompacte
                                            ? 'break-all'
                                            : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                    "
                                    @click="onClickSource(data._id_source)"
                                >
                                    {{
                                        formatIdSource(
                                            data.idsource,
                                            data.idtype
                                        )
                                    }}
                                </span>
                                <span
                                    v-else
                                    class="min-w-0 block"
                                    :class="
                                        getCompacte
                                            ? 'break-all'
                                            : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                    "
                                >
                                    {{
                                        formatIdSource(
                                            data.idsource,
                                            data.idtype
                                        )
                                    }}
                                </span>
                            </div>
                            <!-- Seconde ligne : type · bâtiment · dossier (mobile : ajoutée le · état) -->
                            <div
                                class="company-billing-source-list__source-detail mt-0.5 min-w-0 text-xs text-surface-500"
                                :class="
                                    getCompacte
                                        ? 'whitespace-normal'
                                        : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                "
                                :title="
                                    getCompacte
                                        ? data.etatMobile
                                        : data.detailSource
                                "
                            >
                                <span>{{ data.typeCourt }}</span>
                                <template v-if="getCompacte">
                                    <span aria-hidden="true">{{ " · " }}</span>
                                    <span data-critere="etat">{{
                                        data.etatMobile
                                    }}</span>
                                </template>
                                <template v-else>
                                    <template v-if="data.building?.address">
                                        <span aria-hidden="true">{{
                                            " · "
                                        }}</span>
                                        <span
                                            class="hover:underline cursor-pointer"
                                            data-critere="adresse"
                                            @click="
                                                onClickBuilding(
                                                    data.building._id_building
                                                )
                                            "
                                            >{{ data.building.address }}</span
                                        >
                                    </template>
                                    <template v-if="data.folder?._id_folder">
                                        <span aria-hidden="true">{{
                                            " · "
                                        }}</span>
                                        <span
                                            class="hover:underline cursor-pointer"
                                            data-critere="dossier"
                                            @click="
                                                onClickFolder(
                                                    data.folder._id_folder
                                                )
                                            "
                                            >{{ data.folder.name }}</span
                                        >
                                    </template>
                                </template>
                            </div>
                            <div
                                v-if="getCompacte && data.demandeEnAttente"
                                class="company-billing-source-list__pending text-xs text-surface-600"
                                data-critere="en_attente"
                            >
                                {{ t("state.pending") }}
                            </div>
                        </div>
                        <div
                            v-else
                            class="flex items-center gap-2 overflow-hidden"
                        >
                            <BaseSourceIcon
                                :type="data.idtype"
                                size="24"
                                class="flex-shrink-0"
                            />
                            <span
                                v-if="data.state.state"
                                class="text-primary hover:underline cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap block"
                                @click="onClickSource(data._id_source)"
                            >
                                {{ formatIdSource(data.idsource, data.idtype) }}
                            </span>
                            <span
                                v-else
                                class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap block"
                            >
                                {{ formatIdSource(data.idsource, data.idtype) }}
                            </span>
                        </div>
                    </template>

                    <template #cell_typeFilterValue="{ data }">
                        {{ data.idtype?.toUpperCase() }}
                    </template>

                    <template #cell_addedDate="{ data }">
                        {{ data.addedDate }}
                    </template>

                    <template #cell_stateFilterValue="{ data }">
                        <span
                            v-if="data.state.state && data.demandeEnAttente"
                            class="company-billing-source-list__pending inline-flex items-center gap-1"
                        >
                            {{ t("state.pending") }}
                            <BaseTooltipMessage
                                v-if="data.demandeSuivi"
                                :title="t('demande.tooltip_title')"
                                :content="data.demandeSuivi"
                                position="top"
                            />
                        </span>
                        <span v-else-if="data.state.state">
                            {{ t("state.active") }}
                        </span>
                        <span v-else class="inline-flex items-center gap-1">
                            {{ t("state.removed") }}
                            <BaseTooltipMessage
                                v-if="data.state.removal"
                                :title="t('state_tooltip_title')"
                                :content="data.state.removal"
                                position="top"
                            />
                        </span>
                    </template>

                    <template #cell_ajouteeLe="{ data }">
                        <div
                            v-if="data.cellulesCriteres"
                            class="company-billing-source-list__ajoutee min-w-0 flex-1 whitespace-nowrap text-sm text-surface-800 tabular-nums"
                            data-critere="ajoutee"
                        >
                            {{ data.ajouteeLe }}
                        </div>
                    </template>

                    <template #cell_surLeCompte="{ data }">
                        <div
                            v-if="data.cellulesCriteres"
                            class="min-w-0 flex-1"
                        >
                            <div
                                class="text-sm text-surface-800"
                                data-critere="sur_le_compte"
                            >
                                <span class="whitespace-nowrap">{{
                                    data.cellulesCriteres.surLeCompte.etat
                                }}</span>
                                <span
                                    v-if="
                                        data.cellulesCriteres.surLeCompte.date
                                    "
                                    class="company-billing-source-list__supprimee-le block whitespace-nowrap text-xs text-surface-600 tabular-nums"
                                    data-critere="supprimee_le"
                                    >{{
                                        data.cellulesCriteres.surLeCompte.date
                                    }}</span
                                >
                                <BaseTooltipMessage
                                    v-if="
                                        !data.cellulesCriteres.surLeCompte
                                            .surLeCompte && data.state.removal
                                    "
                                    :title="t('state_tooltip_title')"
                                    :content="data.state.removal"
                                    position="top"
                                />
                            </div>
                            <div
                                v-if="data.demandeEnAttente"
                                class="company-billing-source-list__pending mt-1 text-xs text-surface-600"
                            >
                                {{ t("state.pending") }}
                                <BaseTooltipMessage
                                    v-if="data.demandeSuivi"
                                    :title="t('demande.tooltip_title')"
                                    :content="data.demandeSuivi"
                                    position="top"
                                />
                            </div>
                        </div>
                    </template>

                    <template #cell_miseAJourLe="{ data }">
                        <div
                            v-if="data.cellulesCriteres"
                            class="min-w-0 flex-1 text-sm"
                            :class="{
                                'italic text-surface-500':
                                    data.cellulesCriteres.miseAJour.nonMesure,
                                'text-surface-500':
                                    data.cellulesCriteres.miseAJour.sansObjet,
                                'text-surface-800':
                                    !data.cellulesCriteres.miseAJour
                                        .nonMesure &&
                                    !data.cellulesCriteres.miseAJour.sansObjet,
                            }"
                            data-critere="mise_a_jour"
                        >
                            <span
                                :class="
                                    data.cellulesCriteres.miseAJour.sansObjet
                                        ? 'block line-clamp-2 text-balance'
                                        : 'whitespace-nowrap tabular-nums'
                                "
                                >{{ data.miseAJourCourt }}</span
                            >
                            <BaseTooltipMessage
                                v-if="
                                    data.cellulesCriteres.miseAJour.origine &&
                                    !data.cellulesCriteres.miseAJour.sansObjet
                                "
                                :title="t('mise_a_jour_tooltip_title')"
                                :content="
                                    data.cellulesCriteres.miseAJour.origine
                                "
                                position="top"
                            />
                        </div>
                    </template>

                    <template #cell_siSupprimee="{ data }">
                        <span
                            v-if="
                                data.siSupprimeeCell &&
                                data.siSupprimeeCell.libere === null
                            "
                            class="italic text-surface-500"
                            data-critere="si_supprimee"
                        >
                            {{ data.siSupprimeeCell.texte }}
                        </span>
                        <BaseTag
                            v-else-if="data.siSupprimeeCell"
                            :value="data.siSupprimeeCell.texte"
                            v-bind="
                                etiquetteDevenir(
                                    devenirSiSupprimee(data.siSupprimeeCell)
                                )
                            "
                            :data-devenir="
                                devenirSiSupprimee(data.siSupprimeeCell)
                            "
                            data-critere="si_supprimee"
                        />
                    </template>

                    <template #cell_place="{ data }">
                        <div v-if="data.placeCell" class="min-w-0 flex-1">
                            <span
                                v-if="data.placeCell.libre === null"
                                class="italic text-sm text-surface-500"
                                data-critere="place"
                            >
                                {{ data.placeCell.texte }}
                            </span>
                            <BaseTag
                                v-else
                                :value="data.placeCell.texte"
                                v-bind="
                                    etiquetteDevenir(data.placeCell.devenir)
                                "
                                :data-devenir="data.placeCell.devenir"
                                class="max-w-full whitespace-normal! text-left"
                                data-critere="place"
                            />
                        </div>
                    </template>

                    <template #cell_billing="{ data }">
                        <div
                            v-if="data.cellulesCriteres"
                            class="min-w-0 flex-1 text-sm font-medium text-surface-800 tabular-nums"
                            data-critere="facturee"
                        >
                            <template
                                v-for="(morceau, k) in morceauxInsecables(
                                    data.factureeCourte
                                )"
                                :key="k"
                            >
                                {{ k > 0 ? " " : ""
                                }}<span class="whitespace-nowrap">{{
                                    morceau
                                }}</span>
                            </template>
                        </div>
                        <div
                            v-else-if="data.billingCell"
                            class="company-billing-source-list__billing flex flex-col gap-0.5"
                        >
                            <span
                                class="text-sm font-medium text-surface-800"
                                data-critere="facturee"
                            >
                                {{ data.billingCell.reponse }}
                            </span>
                            <span
                                v-if="data.billingCell.motif"
                                class="text-xs text-surface-500"
                            >
                                {{ data.billingCell.motif }}
                            </span>
                        </div>
                        <template v-else>
                            {{
                                data.billing
                                    ? t("billing.yes")
                                    : t("billing.no")
                            }}
                        </template>
                    </template>

                    <template #cell_folder="{ data }">
                        <span
                            v-if="data.folder?._id_folder"
                            class="text-primary hover:underline cursor-pointer"
                            @click="onClickFolder(data.folder._id_folder)"
                        >
                            {{ data.folder.name }}
                        </span>
                    </template>

                    <template #cell_building="{ data }">
                        <span
                            v-if="data.building?.address"
                            class="text-primary hover:underline cursor-pointer"
                            @click="onClickBuilding(data.building._id_building)"
                        >
                            {{ data.building.address }}
                        </span>
                    </template>

                    <template #cell_owner="{ data }">
                        <span v-if="data.owner">
                            {{
                                t("owner_from_owner", {
                                    details: data.owner.details,
                                })
                            }}
                        </span>
                    </template>

                    <template #cell_actions="{ data }">
                        <div
                            v-if="data.demandeEnAttente"
                            class="company-billing-source-list__actions flex flex-wrap gap-2"
                        >
                            <BaseButton
                                :label="t('demande.relancer')"
                                icon="pi pi-send"
                                size="small"
                                text
                                :disabled="demandeEnCours !== null"
                                :loading="
                                    demandeEnCours === data.demandeEnAttente._id
                                "
                                data-action="relancer"
                                @click="
                                    onRelancerDemande(data.demandeEnAttente)
                                "
                            />
                            <BaseButton
                                :label="t('demande.supprimer')"
                                icon="pi pi-trash"
                                size="small"
                                text
                                severity="danger"
                                :disabled="demandeEnCours !== null"
                                data-action="supprimer"
                                @click="
                                    onSupprimerDemande(data.demandeEnAttente)
                                "
                            />
                        </div>
                    </template>

                    <template #empty>
                        <BaseMessage severity="info" class="w-full">
                            {{ t("no_sources") }}
                        </BaseMessage>
                    </template>
                </BaseTable>
            </div>
        </div>
        <BaseConfirmDialog :group="componentId" />
    </div>
</template>

<style scoped>
/* ================================ */
/* TABLEAU AVEC CRITÈRES (demande du 16/10) */
/* ================================ */
/* Largeur du conteneur, au moins la largeur minimale (défilement en dessous),
   colonnes en pourcentage posées sur les en-têtes, cellules alignées en haut */
.company-billing-source-list__defilement--fixe :deep(table) {
    width: 100%;
    min-width: var(--liste-largeur-min, 0);
    table-layout: fixed;
}

.company-billing-source-list__defilement--fixe :deep(th),
.company-billing-source-list__defilement--fixe :deep(td) {
    vertical-align: top;
    padding-inline: 0.5rem;
}

.company-billing-source-list__defilement--fixe :deep(th:first-child),
.company-billing-source-list__defilement--fixe :deep(td:first-child) {
    padding-inline-start: 1rem;
}

.company-billing-source-list__defilement--fixe :deep(thead th:nth-child(1)) {
    width: var(--liste-colonne-1);
}
.company-billing-source-list__defilement--fixe :deep(thead th:nth-child(2)) {
    width: var(--liste-colonne-2);
}
.company-billing-source-list__defilement--fixe :deep(thead th:nth-child(3)) {
    width: var(--liste-colonne-3);
}
.company-billing-source-list__defilement--fixe :deep(thead th:nth-child(4)) {
    width: var(--liste-colonne-4);
}
.company-billing-source-list__defilement--fixe :deep(thead th:nth-child(5)) {
    width: var(--liste-colonne-5);
}
.company-billing-source-list__defilement--fixe :deep(thead th:nth-child(6)) {
    width: var(--liste-colonne-6);
}
.company-billing-source-list__defilement--fixe :deep(thead th:nth-child(7)) {
    width: var(--liste-colonne-7);
}
</style>
